import React, { useState } from "react";
import AttachmentAssessment from "../../../Create_Assessment/Attachment/AttachmentAssessment";
import ZoomableImage from "../../../../../Components/zoomablePic";
import { iconData } from "../../../../../iconData";
import { IP } from "../../../../../internalServer";
import PdfViewer from "../../../../../Components/QuestionEditCreate/PdfViewer";
import { Box, Paper, Typography, Chip, Divider } from "@mui/material";
import { CheckCircle2 } from "lucide-react";

const ACCENT = "#f77f00";
const ACCENT_SOFT = "#fff4e5";
const BORDER = "#e6e9ef";
const TEXT_PRIMARY = "#1f2733";
const TEXT_SECONDARY = "#5b6472";
const BG_SOFT = "#f8f9fb";
const CORRECT_BG = "#e8f7ee";
const CORRECT_BORDER = "#22c55e";

const PublishedMcqType = ({ questionNumber, question }) => {
  const [mideaviewer, setMideaviewer] = useState(false);
  const [responseAttach, setResponseAttach] = useState("");

  if (!question) return <></>;

  const handleClickforpdf = (fileUrl) => {
    setResponseAttach(fileUrl);
    setMideaviewer(true);
  };

  // 🔹 correct answer set
  const correctOptionKeys = new Set(
    (question?.correct_answers || [])
      .map((ans) => ans?.option_key)
      .filter(Boolean),
  );

  const metaCards = [
    { label: "CHAPTER", value: question?.chapter?.chapterName },
    { label: "SKILLS", value: question?.skill?.name },
    { label: "TYPE OF QUESTION", value: question?.type?.name },
    { label: "DIFFICULTY", value: question?.difficulty_level?.name },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        borderRadius: "14px",
        border: `1px solid ${BORDER}`,
        background: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: ACCENT,
          boxShadow: "0 4px 16px rgba(247,127,0,0.08)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${ACCENT} 0%, #d35400 100%)`,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "13px",
              fontWeight: 700,
              flexShrink: 0,
              boxShadow: "0 3px 8px rgba(247,127,0,0.3)",
            }}
          >
            Q{questionNumber}
          </Box>

          <Typography
            sx={{
              fontSize: "14.5px",
              fontWeight: 600,
              color: TEXT_PRIMARY,
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {question?.title?.split("\n").map((line, i) => (
              <React.Fragment key={i}>
                {i !== 0 && <br />}
                <span>{line}</span>
              </React.Fragment>
            ))}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {question?.correct_score != null && (
            <Chip
              label={`${question.correct_score} Marks`}
              size="small"
              sx={{
                background: ACCENT_SOFT,
                color: ACCENT,
                fontWeight: 700,
                fontSize: "11.5px",
                border: `1px solid ${ACCENT}`,
              }}
            />
          )}
          {question?.is_mandatory && (
            <Chip
              label="* Mandatory"
              size="small"
              sx={{
                background: "#fff0f0",
                color: "#dc3545",
                fontWeight: 600,
                fontSize: "10.5px",
                border: "1px solid #f5c2c7",
              }}
            />
          )}
        </Box>
      </Box>

      {/* Attachments */}
      {question?.attachments?.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {question.attachments.map((attachment, index) => {
            const ext = attachment?.split(".").pop().toLowerCase();
            const isImage = ["jpg", "jpeg", "png"].includes(ext);
            return (
              <Box key={index}>
                {isImage ? (
                  <ZoomableImage src={attachment} />
                ) : (
                  <AttachmentAssessment attachment={attachment} />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Meta grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 1,
          mb: 1.5,
        }}
      >
        {metaCards.map((m, i) => (
          <Box
            key={i}
            sx={{
              p: 1,
              px: 1.25,
              background: BG_SOFT,
              border: `1px solid ${BORDER}`,
              borderRadius: "10px",
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: TEXT_SECONDARY,
                letterSpacing: "0.4px",
                mb: 0.25,
              }}
            >
              {m.label}
            </Typography>
            <Typography
              sx={{
                fontSize: "12.5px",
                fontWeight: 600,
                color: TEXT_PRIMARY,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={m.value}
            >
              {m.value || "—"}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Add-for-homework chip */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Chip
          label={`Add for Homework: ${
            question?.add_to === "homework" ? "Yes" : "No"
          }`}
          size="small"
          sx={{
            background: "#fff",
            color: TEXT_PRIMARY,
            fontWeight: 600,
            fontSize: "11px",
            border: `1px solid ${BORDER}`,
          }}
        />
      </Box>

      <Divider sx={{ borderColor: BORDER, my: 1.5 }} />

      {/* Options with correct highlighting */}
      {question?.options?.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {question.options.map((option, index) => {
            const isCorrect = correctOptionKeys.has(option?.key);
            return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 1.25,
                  px: 1.5,
                  borderRadius: "10px",
                  border: `1.5px solid ${isCorrect ? CORRECT_BORDER : BORDER}`,
                  background: isCorrect ? CORRECT_BG : BG_SOFT,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: isCorrect ? CORRECT_BORDER : "#fff",
                    border: `1.5px solid ${
                      isCorrect ? CORRECT_BORDER : BORDER
                    }`,
                    color: isCorrect ? "#fff" : TEXT_SECONDARY,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12.5px",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {option?.key}
                </Box>
                <Typography
                  sx={{
                    fontSize: "13.5px",
                    color: TEXT_PRIMARY,
                    fontWeight: isCorrect ? 600 : 400,
                    flex: 1,
                    wordBreak: "break-word",
                  }}
                >
                  {option?.value}
                </Typography>
                {isCorrect && (
                  <Chip
                    icon={<CheckCircle2 size={12} color="#fff" />}
                    label="Correct"
                    size="small"
                    sx={{
                      background: CORRECT_BORDER,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "10.5px",
                      height: 22,
                      "& .MuiChip-icon": { ml: "6px", color: "#fff" },
                    }}
                  />
                )}

                {/* Option attachment */}
                {option.attachment &&
                  (() => {
                    const fileUrl =
                      typeof option.attachment === "string"
                        ? option.attachment
                        : option.attachment?.url;
                    if (!fileUrl) return null;

                    const fileType = fileUrl.split(".").pop().toLowerCase();
                    const isImg = ["jpg", "jpeg", "png"].includes(fileType);
                    const isVideo = ["mp4", "mov"].includes(fileType);
                    const isPdf = fileType === "pdf";
                    const fileIcon =
                      iconData?.find((i) => i.extension === fileType)?.icon ||
                      "/images/file_icon.png";
                    const fullUrl = fileUrl.startsWith("http")
                      ? fileUrl
                      : `${IP}${fileUrl}`;

                    return (
                      <Box sx={{ ml: "auto" }}>
                        {isImg ? (
                          <ZoomableImage src={option.attachment} />
                        ) : isVideo ? (
                          <video
                            src={fullUrl}
                            style={{
                              width: 60,
                              height: 50,
                              borderRadius: 6,
                            }}
                            controls
                          />
                        ) : isPdf ? (
                          <Box
                            onClick={() => handleClickforpdf(fileUrl)}
                            sx={{
                              width: 60,
                              height: 50,
                              background: "#fff",
                              border: `1px solid ${BORDER}`,
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={fileIcon}
                              alt="pdf"
                              style={{ width: 22, height: 22 }}
                            />
                          </Box>
                        ) : (
                          <a
                            href={`https://docs.google.com/gview?url=${encodeURIComponent(
                              fullUrl,
                            )}&embedded=true`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none" }}
                          >
                            <Box
                              sx={{
                                width: 60,
                                height: 50,
                                background: "#fff",
                                border: `1px solid ${BORDER}`,
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={fileIcon}
                                alt="file"
                                style={{ width: 22, height: 22 }}
                              />
                            </Box>
                          </a>
                        )}
                      </Box>
                    );
                  })()}
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Reason for correct answer */}
      {question?.correct_answers?.some(
        (ans) => ans?.reason_for_answer?.explanation,
      ) && (
        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: "10px",
            background: "#eef6ff",
            border: "1px solid #cfe3ff",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#1a6fd1",
              letterSpacing: "0.4px",
              mb: 0.5,
            }}
          >
            REASON FOR CORRECT ANSWER
          </Typography>
          {question.correct_answers.map((ans, i) =>
            ans?.reason_for_answer?.explanation ? (
              <Typography
                key={i}
                sx={{
                  fontSize: "13px",
                  color: TEXT_PRIMARY,
                  lineHeight: 1.5,
                }}
              >
                {ans.reason_for_answer.explanation}
              </Typography>
            ) : null,
          )}
        </Paper>
      )}

      {mideaviewer && (
        <PdfViewer
          setMideaviewer={setMideaviewer}
          attachment={responseAttach}
        />
      )}
    </Paper>
  );
};

export default PublishedMcqType;
